import { connect } from 'react-redux';
import { useContext, useMemo } from 'react';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import {
  failExecutionSelectOptions,
  successExecutionSelectOptions,
} from 'KnmPackage/MainModule/constants/agreementsExecutionSelectOptions';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { getKnmErknmPositionsSelectOptions, getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  getDecisionFormRef,
  getDecisionPositionsSelectOptions,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';

import styles from './AgreementsExecution.module.scss';
import { AgreementsSupportingDocumentsAttachment } from '../AgreementsSupportingDocuments/AgreementsSupportingDocumentsAttachment';

interface IAgreementsExecutionProps {
  agreement: IErknmFormAgreement;
  areAllViolationCompleted: boolean;
  id: string;
  isWatchOnly: boolean;
  name: string;
}

interface IAgreementsExecutionStoreProps {
  decisionFormRef: IDecisionFormRef | undefined;
  decisionPositionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  formRef: IKnmFormRef | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
}

const AgreementsExecutionWithoutStore = ({
  agreement,
  areAllViolationCompleted,
  id,
  isWatchOnly,
  name,
  decisionFormRef,
  decisionPositionsSelectOptions,
  formRef,
  positionsSelectOptions,
}: IAgreementsExecutionProps & IAgreementsExecutionStoreProps) => {
  const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
  const fieldName = `${name}.execution`;

  const decisionSelectOptions = useMemo(() => {
    const options: ISelectOption<number>[] = [];

    if (areAllViolationCompleted) {
      options.push(successExecutionSelectOptions);
    }

    options.push(failExecutionSelectOptions);

    return options;
  }, [areAllViolationCompleted]);

  return (
    <FieldBlock label={'Сведения об исполнении соглашения'} className={styles.Wrapper}>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Решение об исполнении соглашения',
          field: `${fieldName}.decision`,
          isKnm,
          isPm,
          isDecision,
        })}
        color="black"
        className={styles.FieldBlock}
        id={`${id}.execution.decision`}
        placementLabel="left"
      >
        <SelectInputField
          name={`${fieldName}.decision`}
          placeholder="Решение"
          options={decisionSelectOptions}
          size="medium"
          disabled={isWatchOnly}
        />
      </FieldBlock>
      <Hint text="Для признания соглашения исполненным все нарушения ОТ, включенные в соглашение, должны быть устранены (устранение нарушений фиксируется в каждом отдельном нарушении)" />

      {(agreement?.execution?.decision?.value === 1 || agreement?.execution?.decision?.value) && (
        <FieldBlock
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
          id={`${id}.execution.executionDate`}
          label={getTitleFieldWithRedStar({
            label: 'Фактическая дата исполнения соглашения',
            field: `${fieldName}.executionDate`,
            isKnm,
            isPm,
            isDecision,
          })}
        >
          <div>
            <DatePickerField
              name={`${fieldName}.executionDate`}
              placeholder={'Фактическая дата'}
              size="medium"
              minDate={new Date()}
              className={styles.DatePicker}
              disabled={isWatchOnly}
            />
          </div>
        </FieldBlock>
      )}

      <AgreementsSupportingDocumentsAttachment
        id={`${id}.execution.attachments`}
        watchOnly={isWatchOnly}
        name={`${fieldName}.attachments`}
        formRef={isDecision ? decisionFormRef : formRef}
        title={getTitleFieldWithRedStar({
          label: 'Представленные контролируемым лицом (учредителем) документы',
          field: `${fieldName}.attachments`,
          isKnm,
          isPm,
          isDecision,
        })}
        acceptedFiles={'.pdf'}
      />

      <FieldBlock label={'Решение принято'} margin="top">
        <FieldBlock
          id={`${id}.execution.fioSigner`}
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
          label={getTitleFieldWithRedStar({
            label: 'ФИО принявшего решение',
            field: `${fieldName}.fioSigner`,
            isKnm,
            isPm,
            isDecision,
          })}
        >
          <TextInputField name={`${fieldName}.fioSigner`} placeholder="ФИО" disabled={isWatchOnly} />
        </FieldBlock>
        <FieldBlock
          id={`${id}.execution.titleSigner`}
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
          label={getTitleFieldWithRedStar({
            label: 'Должность лица, принявшего решение',
            field: `${fieldName}.titleSigner`,
            isKnm,
            isPm,
            isDecision,
          })}
        >
          <SelectInputField
            name={`${fieldName}.titleSigner`}
            placeholder="Выберите должность"
            options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
            isSearchable
            size="medium"
            disabled={isWatchOnly}
          />
        </FieldBlock>
      </FieldBlock>
    </FieldBlock>
  );
};
const mapStateToProps = (state: IRootState): IAgreementsExecutionStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
});

export const AgreementsExecution = connect(mapStateToProps)(AgreementsExecutionWithoutStore);
